import React from "react";
import styles from "./RetroFond.module.css";
import {DESSINS, NOMS_OBJETS} from "./sprites";
import {PALETTES, REGLAGES} from "./reglages";
import {graine, rectangles} from "../arcade/sprites";
import {resoudre} from "../reglages";

/**
 * Fonds du thème Rétro 8 bits : trois ambiances de jeu vidéo d'autrefois, en SVG et CSS purs (dessins et code originaux,
 * aucun personnage, logo ni sprite d'un jeu existant). Un seul composant, trois variantes (`variante`) :
 *   paysage  : collines, nuages et sol de briques qui défilent en parallaxe ;
 *   objets   : consoles, manettes et cartouches génériques qui flottent vers le haut ;
 *   pieces   : pluie de pièces et de cœurs sur un fond à damier.
 * Tout bouge par `transform` ou `opacity` ; les positions sont tirées une fois avec une graine fixe.
 */
const RECTS = Object.keys(DESSINS).reduce((acc, nom) => ({...acc, [nom]: rectangles(DESSINS[nom])}), {});

function Sprite({nom, couleur, className}) {
    const hauteur = DESSINS[nom].length, largeur = DESSINS[nom][0].length;
    return (
        <svg viewBox={`0 0 ${largeur} ${hauteur}`} shapeRendering="crispEdges" className={className} fill={couleur}>
            {RECTS[nom].map((r, i) => <rect key={i} x={r.x} y={r.y} width={r.l} height={1}/>)}
        </svg>
    );
}

function Paysage({nombre, palette}) {
    const hasard = graine(5);
    return (
        <>
            {Array.from({length: Math.min(nombre, 8)}, (_, i) => (
                <div key={i} className={styles.nuage} style={{top: `${4 + hasard() * 40}%`, animationDelay: `${-hasard() * 60}s`, animationDuration: `${50 + hasard() * 40}s`}}>
                    <Sprite nom="nuage" couleur={palette.nuage} className={styles.sprite}/>
                </div>
            ))}
            <div className={`${styles.collines} ${styles.loin}`} style={{background: palette.loin}}/>
            <div className={`${styles.collines} ${styles.pres}`} style={{background: palette.pres}}/>
            <div className={styles.sol} style={{"--sol": palette.sol, "--motif": palette.motif}}><div className={styles.briques}/></div>
        </>
    );
}

function Objets({nombre, palette}) {
    const hasard = graine(17);
    return Array.from({length: nombre}, (_, i) => (
        <div key={i} className={styles.flotte} style={{left: `${hasard() * 90}%`, animationDelay: `${-hasard() * 40}s`, animationDuration: `${26 + hasard() * 16}s`}}>
            <div className={styles.balance} style={{animationDelay: `${-hasard() * 4}s`}}>
                <Sprite nom={NOMS_OBJETS[i % NOMS_OBJETS.length]} couleur={palette.objets[i % palette.objets.length]} className={styles.objet}/>
            </div>
        </div>
    ));
}

function Pieces({nombre, palette}) {
    const hasard = graine(29);
    return Array.from({length: nombre * 2}, (_, i) => (
        <div key={i} className={styles.tombe} style={{left: `${hasard() * 94}%`, animationDelay: `${-hasard() * 24}s`, animationDuration: `${16 + hasard() * 10}s`}}>
            <Sprite nom={i % 3 === 0 ? "coeur" : "piece"} couleur={i % 3 === 0 ? "#ff5d73" : "#ffcf33"} className={styles.petit}/>
        </div>
    ));
}

const VARIANTES = {paysage: Paysage, objets: Objets, pieces: Pieces};

export default function RetroFond({variante = "paysage", reglages}) {
    const r = resoudre(REGLAGES, reglages);
    const palette = PALETTES[r.palette] || PALETTES.jour;
    const Variante = VARIANTES[variante] || Paysage;
    const ciel = variante === "pieces"
        ? {background: `repeating-conic-gradient(rgba(255, 255, 255, 0.14) 0 25%, transparent 0 50%) 0 0 / 8% 8%, ${palette.haut}`}
        : {background: `linear-gradient(180deg, ${palette.haut}, ${palette.bas})`};
    return (
        <div className={styles.scene} aria-hidden="true" style={{...ciel, "--vitesse": 1 / r.vitesse}}>
            <Variante nombre={r.nombre} palette={palette}/>
        </div>
    );
}

export const fondRetro = variante => function FondRetro(props) {
    return <RetroFond variante={variante} {...props}/>;
};
